<template>
  <header
    ref="headerSection"
    class="relative w-full py-24 md:py-32 lg:py-36 overflow-hidden bg-white"
  >
    <!-- Subtle background elements -->
    <div class="absolute inset-0 z-0 overflow-hidden">
      <!-- Minimal shapes -->
      <div class="absolute top-0 right-0 w-1/2 h-1/2 bg-gray-50 rounded-bl-[100px]" />
      <div class="absolute -bottom-10 -left-10 w-32 h-32 border border-gray-200 rounded-full" />
      <div class="absolute top-1/4 right-1/4 w-16 h-16 border border-gray-200 rotate-45" />
    </div>
    
    <div class="max-w-screen-xl mx-auto px-4 relative z-10">
      <div class="flex flex-col lg:flex-row items-center">
        <!-- Text content -->
        <div class="w-full lg:w-1/2 mb-16 lg:mb-0 lg:pr-12">
          <div class="mb-4">
            <div class="inline-block h-1 w-10 bg-blue-600 mb-2" />
            <span class="text-sm uppercase tracking-wider text-gray-500 font-medium">Winnode - Your Trusted</span>
          </div>
          
          <h1 
            class="text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 text-gray-900"
            :style="{ transform: `translate3d(0, ${parallaxPos.y * -0.01}px, 0)` }"
          >
            Validator<br><span class="text-blue-600">RPC Service Provider</span>
          </h1>
          
          <p 
            class="text-gray-600 text-lg md:text-xl mb-8 max-w-lg leading-relaxed"
            :style="{ transform: `translate3d(0, ${parallaxPos.y * -0.005}px, 0)` }"
          >
            Runs a secure and reliable testnet validator node, ensuring high uptime, transparency, decentralization, and optimal performance.
          </p>
          
          <div class="flex flex-wrap gap-4">
            <a 
              href="https://status.winnode.xyz" 
              class="group flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-sm"
            >
              <span class="font-medium">View Run Upime Validator</span>
              <svg
                class="w-4 h-4 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </a>
          </div>
          
          <!-- Simple stats -->
          <div class="flex flex-wrap gap-16 mt-12 border-t border-gray-100 pt-8">
            <div class="flex flex-col">
              <span class="text-4xl font-bold text-gray-900">99.9%</span>
              <span class="text-gray-500 text-sm mt-1">Uptime</span>
            </div>
            <div class="flex flex-col">
              <span class="text-4xl font-bold text-gray-900">24+</span>
              <span class="text-gray-500 text-sm mt-1">Networks</span>
            </div>
            <div class="flex flex-col">
              <span class="text-4xl font-bold text-gray-900">$735K</span>
              <span class="text-gray-500 text-sm mt-1">Value</span>
            </div>
          </div>
        </div>
        
        <!-- Minimalist Illustration -->
        <div 
          class="w-full lg:w-1/2"
          :style="{ transform: `translate3d(${parallaxPos.x * 0.01}px, ${parallaxPos.y * 0.01}px, 0)` }"
        >
          <div class="relative mx-auto max-w-md lg:max-w-lg">
            <!-- Clean frame -->
            <div class="absolute inset-2 border border-gray-200 rounded-2xl" />
            
            <!-- Main image with minimal styling -->
            <div class="relative z-10 rounded-2xl overflow-hidden shadow-sm">
              <img 
                src="@/assets/img-web/header.png" 
                alt="Winnode Blockchain Infrastructure"
                class="relative z-10 w-full h-auto" 
              >
              
              <!-- Subtle color accent -->
              <div class="absolute left-0 top-0 h-full w-1 bg-blue-600" />
            </div>
            
            <!-- Subtle accent element -->
            <div class="absolute -bottom-3 -right-3 w-24 h-24 bg-blue-50 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'MinimalistHeaderComponent',
  setup() {
    const headerSection = ref(null);
    const parallaxPos = ref({ x: 0, y: 0 });
    
    const handleMouseMove = (e) => {
      const rect = headerSection.value.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      
      // Reduced parallax effect for minimalist design
      parallaxPos.value = { 
        x: x * 0.5, 
        y: y * 0.5 
      };
    };
    
    // Add and remove event listeners
    onMounted(() => {
      window.addEventListener('mousemove', handleMouseMove);
    });
    
    onBeforeUnmount(() => {
      window.removeEventListener('mousemove', handleMouseMove);
    });
    
    return {
      headerSection,
      parallaxPos
    };
  }
};
</script>

<style scoped>
/* Smooth transition for parallax movement */
[style*="transform"] {
  transition: transform 0.2s ease-out;
}
</style>
